import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Flame, Shield, Crown, Zap, AlertTriangle, X } from "lucide-react";

export default function StreakSaveModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSaveFlow();
    }
  }, [open]);

  const fetchSaveFlow = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/streaks/save-flow`);
      setFlow(res.data);
    } catch (error) {
      console.error("Failed to fetch streak save flow:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/streaks/save-action`, { action });
      
      if (res.data.action === "redirect") {
        onOpenChange(false);
        navigate(res.data.to);
      } else if (res.data.success) {
        toast.success(`Streak protected until ${new Date(res.data.expires_at).toLocaleDateString()}`);
        onOpenChange(false);
      } else if (res.data.action === "dismissed") {
        onOpenChange(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save streak");
    } finally {
      setActing(false);
    }
  };

  if (!flow?.show_flow) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${
                flow.urgency === 'critical' ? 'text-red-500' : 
                flow.urgency === 'warning' ? 'text-amber-500' : 'text-yellow-500'
              }`} />
              Streak at Risk!
            </DialogTitle>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Streak Display */}
            <div className="text-center py-4">
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                flow.urgency === 'critical' ? 'bg-red-500/20 animate-pulse' : 
                flow.urgency === 'warning' ? 'bg-amber-500/20' : 'bg-yellow-500/20'
              }`}>
                <Flame className={`w-10 h-10 ${
                  flow.urgency === 'critical' ? 'text-red-500' : 
                  flow.urgency === 'warning' ? 'text-amber-500' : 'text-yellow-500'
                }`} />
              </div>
              <h3 className="text-2xl font-bold">{flow.streak_at_risk}-day streak</h3>
              <p className="text-muted-foreground mt-1">{flow.subheadline}</p>
            </div>

            {/* Urgency Badge */}
            <div className={`text-center py-2 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 mx-auto ${
              flow.urgency === 'critical' ? 'bg-red-500/20 text-red-600' : 
              flow.urgency === 'warning' ? 'bg-amber-500/20 text-amber-600' : 'bg-yellow-500/20 text-yellow-600'
            }`}>
              <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
              {flow.hours_remaining} hours remaining
            </div>

            {/* Options */}
            <div className="space-y-3">
              {flow.options?.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAction(option.type)}
                  disabled={acting}
                  className={`w-full p-4 rounded-xl border text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${
                    option.free 
                      ? 'bg-green-500/10 border-green-500/30 hover:border-green-500/50' 
                      : 'bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      option.free ? 'bg-green-500/20' : 'bg-primary/20'
                    }`}>
                      {option.type === 'use_freeze' && <Shield className="w-5 h-5 text-green-500" />}
                      {option.type === 'upgrade_pro' && <Zap className="w-5 h-5 text-primary" />}
                      {option.type === 'upgrade_duo' && <Crown className="w-5 h-5 text-amber-500" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{option.label}</div>
                      <div className="text-sm text-muted-foreground">{option.description}</div>
                    </div>
                    {option.cta && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                        {option.cta}
                      </span>
                    )}
                    {option.free && (
                      <span className="text-xs font-medium text-green-600 bg-green-500/10 px-2 py-1 rounded-full">
                        Free
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Dismiss */}
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => handleAction("dismiss")}
              disabled={acting}
            >
              I'll visit the app instead
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
